<template>
  <teleport to="body">
    <transition name="confirm-overlay">
      <div
        v-if="state.isOpen"
        class="confirm-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="'confirm-title'"
        :aria-describedby="'confirm-message'"
        @click.self="cancel"
        @keydown.esc="cancel"
      >
        <transition name="confirm-dialog">
          <div
            v-if="state.isOpen"
            class="confirm-dialog"
            ref="dialogRef"
            tabindex="-1"
          >
            <!-- Icon -->
            <div class="confirm-icon" :class="`icon-${state.variant}`">
              <Trash2Icon v-if="state.variant === 'destructive'" :size="22" />
              <AlertTriangleIcon
                v-else-if="state.variant === 'warning'"
                :size="22"
              />
              <HelpCircleIcon v-else :size="22" />
            </div>

            <!-- Content -->
            <div class="confirm-content">
              <h3 id="confirm-title" class="confirm-title">
                {{ state.title }}
              </h3>
              <p id="confirm-message" class="confirm-message">
                {{ state.message }}
              </p>
            </div>

            <!-- Actions -->
            <div class="confirm-actions">
              <button
                class="confirm-btn cancel-btn"
                @click="cancel"
                ref="cancelRef"
              >
                {{ state.cancelLabel }}
              </button>
              <button
                class="confirm-btn"
                :class="`action-${state.variant}`"
                @click="confirm"
                ref="confirmRef"
              >
                {{ state.confirmLabel }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref, watch, nextTick } from "vue";
import {
  confirmDialogState,
  confirmDialogResolve,
} from "@/composables/useConfirm";
import {
  Trash2 as Trash2Icon,
  AlertTriangle as AlertTriangleIcon,
  HelpCircle as HelpCircleIcon,
} from "lucide-vue-next";

export default {
  name: "AppConfirmDialog",
  components: { Trash2Icon, AlertTriangleIcon, HelpCircleIcon },
  setup() {
    const state = confirmDialogState;
    const dialogRef = ref(null);
    const cancelRef = ref(null);
    const confirmRef = ref(null);

    // Auto-focus cancel button (safe default) when dialog opens
    watch(
      () => state.isOpen,
      async (open) => {
        if (open) {
          await nextTick();
          // Focus cancel button first — safer for destructive dialogs
          cancelRef.value?.focus();
        }
      },
    );

    function confirm() {
      confirmDialogResolve(true);
    }

    function cancel() {
      confirmDialogResolve(false);
    }

    return { state, dialogRef, cancelRef, confirmRef, confirm, cancel };
  },
};
</script>

<style scoped>
/* ── Overlay ── */
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

/* ── Dialog box ── */
.confirm-dialog {
  background: white;
  border-radius: 16px;
  padding: 28px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 24px 64px -12px rgba(15, 23, 42, 0.25),
    0 8px 24px -4px rgba(15, 23, 42, 0.08);
  outline: none;
}

/* ── Icon area ── */
.confirm-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
}

.icon-destructive {
  background: #fef2f2;
  color: #ef4444;
}
.icon-warning {
  background: #fffbeb;
  color: #f59e0b;
}
.icon-default {
  background: #eef2ff;
  color: #6366f1;
}

/* ── Content ── */
.confirm-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confirm-title {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}

.confirm-message {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.55;
}

/* ── Actions row ── */
.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.confirm-btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cancel-btn {
  background: white;
  border-color: #e2e8f0;
  color: #475569;
}

.cancel-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.action-destructive {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.action-destructive:hover {
  background: #dc2626;
  border-color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
}

.action-warning {
  background: #f59e0b;
  color: white;
  border-color: #f59e0b;
}

.action-warning:hover {
  background: #d97706;
  border-color: #d97706;
}

.action-default {
  background: #6366f1;
  color: white;
  border-color: #6366f1;
}

.action-default:hover {
  background: #4f46e5;
  border-color: #4f46e5;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35);
}

/* ── Transition animations ── */
.confirm-overlay-enter-active,
.confirm-overlay-leave-active {
  transition: opacity 0.2s ease;
}

.confirm-overlay-enter-from,
.confirm-overlay-leave-to {
  opacity: 0;
}

.confirm-dialog-enter-active {
  animation: dialog-pop-in 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.confirm-dialog-leave-active {
  animation: dialog-pop-out 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes dialog-pop-in {
  from {
    opacity: 0;
    transform: scale(0.93) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes dialog-pop-out {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(4px);
  }
}
</style>
